import {
  createAuthenticatedApi,
  createOpenApiConfiguration,
  getApiErrorMessage,
} from "@/lib/utils/api";
import {
  DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT,
  MAKEUPS_ANNUAL_REQUEST_LIMIT_KEY,
  parseMakeupsAnnualRequestLimit,
} from "@/features/makeups/shared/makeupAnnualLimitSettings";
import {
  DEFAULT_MAKEUPS_ANNUAL_PERIOD,
  MAKEUPS_ANNUAL_PERIOD_KEY,
  parseMakeupsAnnualPeriod,
  type MakeupsAnnualPeriod,
} from "@/features/makeups/shared/makeupAnnualPeriod";
import {
  DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
  MAKEUPS_EXPIRY_IN_DAYS_KEY,
  MAKEUPS_SETTINGS_GROUP,
  parseMakeupsExpiryInDays,
} from "@/features/makeups/shared/makeupExpirySettings";
import { GlobalApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";

export type MakeupsExpiryInDaysResult = {
  days: number;
  persisted: boolean;
};

export type MakeupsAnnualRequestLimitResult = {
  limit: number;
  persisted: boolean;
};

export type MakeupsEligibilitySettingsResult = {
  expiryDays: number;
  annualRequestLimit: number;
  annualPeriod: MakeupsAnnualPeriod;
  persisted: {
    expiry: boolean;
    annualLimit: boolean;
    annualPeriod: boolean;
  };
};

function createGlobalApi(accessToken?: string): GlobalApi {
  if (accessToken) {
    return createAuthenticatedApi(GlobalApi, accessToken);
  }
  return new GlobalApi(createOpenApiConfiguration());
}

function isNotFoundOrBadRequest(error: unknown): boolean {
  return (
    error instanceof ResponseError &&
    (error.response.status === 404 || error.response.status === 400)
  );
}

function settingsMapFromRows(
  rows: { key: string; value: string }[],
): Map<string, string> {
  return new Map(rows.map((row) => [row.key, row.value]));
}

export async function fetchMakeupsEligibilitySettings(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<MakeupsEligibilitySettingsResult> {
  const api = createGlobalApi(options?.accessToken);

  try {
    const rows = await api.apiGlobalSettingsByGroupGet({
      group: MAKEUPS_SETTINGS_GROUP,
      xTenantID: tenantId,
    });
    const map = settingsMapFromRows(rows);
    const expiryRaw = map.get(MAKEUPS_EXPIRY_IN_DAYS_KEY);
    const annualRaw = map.get(MAKEUPS_ANNUAL_REQUEST_LIMIT_KEY);
    const annualPeriodRaw = map.get(MAKEUPS_ANNUAL_PERIOD_KEY);

    return {
      expiryDays: parseMakeupsExpiryInDays(expiryRaw),
      annualRequestLimit: parseMakeupsAnnualRequestLimit(annualRaw),
      annualPeriod: parseMakeupsAnnualPeriod(annualPeriodRaw),
      persisted: {
        expiry: expiryRaw != null,
        annualLimit: annualRaw != null,
        annualPeriod: annualPeriodRaw != null,
      },
    };
  } catch (error) {
    if (!isNotFoundOrBadRequest(error)) {
      // Fall through to defaults when group is missing.
    }
    return {
      expiryDays: DEFAULT_MAKEUPS_EXPIRY_IN_DAYS,
      annualRequestLimit: DEFAULT_MAKEUPS_ANNUAL_REQUEST_LIMIT,
      annualPeriod: DEFAULT_MAKEUPS_ANNUAL_PERIOD,
      persisted: {
        expiry: false,
        annualLimit: false,
        annualPeriod: false,
      },
    };
  }
}

export async function fetchMakeupsExpiryInDays(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<MakeupsExpiryInDaysResult> {
  const settings = await fetchMakeupsEligibilitySettings(tenantId, options);
  return {
    days: settings.expiryDays,
    persisted: settings.persisted.expiry,
  };
}

export async function fetchMakeupsAnnualRequestLimit(
  tenantId: number,
  options?: { accessToken?: string },
): Promise<MakeupsAnnualRequestLimitResult> {
  const settings = await fetchMakeupsEligibilitySettings(tenantId, options);
  return {
    limit: settings.annualRequestLimit,
    persisted: settings.persisted.annualLimit,
  };
}

async function upsertMakeupsSetting(
  tenantId: number,
  key: string,
  value: string,
  accessToken: string,
  persisted: boolean,
  errorLabel: string,
): Promise<void> {
  const api = createAuthenticatedApi(GlobalApi, accessToken);

  const createRequest = {
    xTenantID: tenantId,
    createSettingRequest: {
      key,
      value,
      group: MAKEUPS_SETTINGS_GROUP,
    },
  };
  const updateRequest = {
    xTenantID: tenantId,
    updateSettingRequest: {
      key,
      value,
      group: MAKEUPS_SETTINGS_GROUP,
    },
  };

  const throwUpsertError = async (error: unknown, action: "create" | "update") => {
    const message = await getApiErrorMessage(
      error,
      `Failed to ${action} ${errorLabel}`,
    );
    throw new Error(message);
  };

  if (persisted) {
    try {
      await api.apiGlobalSettingsPut(updateRequest);
      return;
    } catch (error) {
      if (!isNotFoundOrBadRequest(error)) {
        await throwUpsertError(error, "update");
      }
    }
  }

  try {
    await api.apiGlobalSettingsPost(createRequest);
    return;
  } catch (error) {
    if (!(error instanceof ResponseError && error.response.status === 409)) {
      await throwUpsertError(error, "create");
    }
  }

  try {
    await api.apiGlobalSettingsPut(updateRequest);
  } catch (error) {
    await throwUpsertError(error, "update");
  }
}

export async function upsertMakeupsExpiryInDays(
  tenantId: number,
  days: number,
  accessToken: string,
  persisted: boolean,
): Promise<number> {
  const value = String(parseMakeupsExpiryInDays(days));
  await upsertMakeupsSetting(
    tenantId,
    MAKEUPS_EXPIRY_IN_DAYS_KEY,
    value,
    accessToken,
    persisted,
    "makeup expiry setting",
  );
  return parseMakeupsExpiryInDays(value);
}

export async function upsertMakeupsAnnualRequestLimit(
  tenantId: number,
  limit: number,
  accessToken: string,
  persisted: boolean,
): Promise<number> {
  const value = String(parseMakeupsAnnualRequestLimit(limit));
  await upsertMakeupsSetting(
    tenantId,
    MAKEUPS_ANNUAL_REQUEST_LIMIT_KEY,
    value,
    accessToken,
    persisted,
    "makeup annual limit setting",
  );
  return parseMakeupsAnnualRequestLimit(value);
}

export async function upsertMakeupsAnnualPeriod(
  tenantId: number,
  period: MakeupsAnnualPeriod,
  accessToken: string,
  persisted: boolean,
): Promise<MakeupsAnnualPeriod> {
  const value = parseMakeupsAnnualPeriod(period);
  await upsertMakeupsSetting(
    tenantId,
    MAKEUPS_ANNUAL_PERIOD_KEY,
    value,
    accessToken,
    persisted,
    "makeup annual period setting",
  );
  return value;
}

export async function upsertMakeupsEligibilitySettings(
  tenantId: number,
  request: {
    expiryDays: number;
    annualRequestLimit: number;
    annualPeriod: MakeupsAnnualPeriod;
    persisted: MakeupsEligibilitySettingsResult["persisted"];
  },
  accessToken: string,
): Promise<MakeupsEligibilitySettingsResult> {
  const [expiryDays, annualRequestLimit, annualPeriod] = await Promise.all([
    upsertMakeupsExpiryInDays(
      tenantId,
      request.expiryDays,
      accessToken,
      request.persisted.expiry,
    ),
    upsertMakeupsAnnualRequestLimit(
      tenantId,
      request.annualRequestLimit,
      accessToken,
      request.persisted.annualLimit,
    ),
    upsertMakeupsAnnualPeriod(
      tenantId,
      request.annualPeriod,
      accessToken,
      request.persisted.annualPeriod,
    ),
  ]);

  return {
    expiryDays,
    annualRequestLimit,
    annualPeriod,
    persisted: {
      expiry: true,
      annualLimit: true,
      annualPeriod: true,
    },
  };
}
